import { AtticScope, CellarScope } from 'models/purchaseFlow/measurementDTO'
import { BLUE_100, BLUE_400, CORAL_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { MeasurementQuoteLineItemDTO, MeasurementQuoteLineItemType } from 'models/purchaseFlow/measurementQuoteDTO'

import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import { FC } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { bigFromDecimal, bigFromFee } from 'utils/price/decimals'
import { feeHasDiscount, formatPrice, valueAfterDiscount } from 'utils/price'
import { useMeasurementCostAssumption } from './useMeasurementCostAssumption'
import { usePurchaseFlowConfig } from '../../../_main/contexts/PurchaseFlowConfig.context'
import { useTranslation } from 'react-i18next'

/**
 * @component
 * Live cost-assumption breakdown for the On-Site Area Report (ISSUE-2188) — renders the stateless
 * quote line items from the backend pricing engine, debounced on every configuration change. The
 * cart summary below carries the totals; the estimate-only note lives in the step disclaimer. Only
 * shown while a measurement product is in the cart.
 *
 * @example
 * <MeasurementCostAssumption />
 */
export const MeasurementCostAssumption: FC = () => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'measurement_step' })
  // Same catalogue key as ProductList / ProductListing, so the heading can never drift from the main panel's name (ISSUE-3256)
  const { t: tProduct } = useTranslation(['product'])
  const { isActive, quote, isFetching, isError, isPricedPerResidentialUnit, measurementPropertyType, cellarScope, atticScope, measurementProductId } = useMeasurementCostAssumption()
  const { catalogueDiscount } = usePurchaseFlowConfig()

  const engineFillsDiscount = !!quote && feeHasDiscount(quote.net)
  const lineDiscount = (item: MeasurementQuoteLineItemDTO) =>
    engineFillsDiscount && item.amount.discountPercentage !== undefined
      ? bigFromDecimal(item.amount.discountPercentage)
      : catalogueDiscount

  const lineItemLabel = (item: MeasurementQuoteLineItemDTO) => {
    if (item.type === MeasurementQuoteLineItemType.CATEGORY_BASE) {
      // Unit-based categories (Kat. 3/4) call their base line "First residential unit" per design — keyed on the
      // quote actually being priced per unit, so a category the engine prices per floor falls back to the base label
      if (isPricedPerResidentialUnit) return t('cost_assumption.line.FIRST_RESIDENTIAL_UNIT')

      const propertyType = t(`property_type_card.${measurementPropertyType}`)

      // A House base bundles the floors it covers, and floors beyond them price differently on their own line.
      // Naming the count is what makes 490 € legible as two floors rather than one opaque base (ISSUE-2861).
      return item.quantity
        ? t('cost_assumption.line.CATEGORY_BASE_FLOORS', { propertyType, count: item.quantity })
        : t('cost_assumption.line.CATEGORY_BASE', { propertyType })
    }
    if (item.type === MeasurementQuoteLineItemType.RESIDENTIAL_UNIT) {
      return t('cost_assumption.line.RESIDENTIAL_UNIT', { count: item.quantity ?? 1 })
    }
    if (item.type === MeasurementQuoteLineItemType.ADDITIONAL_FLOOR) {
      return t('cost_assumption.line.ADDITIONAL_FLOOR', { quantity: item.quantity ?? 1 })
    }
    if (item.type === MeasurementQuoteLineItemType.CELLAR && cellarScope === CellarScope.ROOM) {
      return t('cost_assumption.line.CELLAR_ROOM')
    }
    if (item.type === MeasurementQuoteLineItemType.CELLAR && cellarScope === CellarScope.PRESENT_NOT_MEASURED) {
      return t('cost_assumption.line.CELLAR_PRESENT_NOT_MEASURED')
    }
    if (item.type === MeasurementQuoteLineItemType.ATTIC && atticScope === AtticScope.PRESENT_NOT_MEASURED) {
      return t('cost_assumption.line.ATTIC_PRESENT_NOT_MEASURED')
    }
    return t(`cost_assumption.line.${item.type}`)
  }

  return (
    <Collapse in={isActive} timeout={300} unmountOnExit>
      <Stack
        gap=".8rem"
        data-testid="measurement-cost-assumption"
        sx={{ backgroundColor: BLUE_100, border: `1px solid ${BLUE_400}`, borderRadius: '.8rem', padding: '1.2rem', marginBottom: '1.2rem' }}
      >

        <Stack direction="row" justifyContent="space-between" alignItems="center">

          <Typography variant="text-sm" color={GRAY_700} fontWeight={550}>{t('cost_assumption.title')}</Typography>

          {isFetching && <CircularProgress size={14} />}

        </Stack>

        <Typography variant="text-md" color={GRAY_900} fontWeight={600}>{tProduct(`p_${measurementProductId}`)}</Typography>

        {/* A failed quote never presents stale amounts as current — the line items collapse and this note explains why */}
        {isError && <Typography variant="text-sm" color={CORAL_600}>{t('cost_assumption.error')}</Typography>}

        {/* Dimmed while a fresh quote is in flight — prices fade back in when the new amounts land */}
        <Collapse in={!!quote} timeout={300}>
          <Stack gap=".4rem" sx={{ opacity: isFetching ? 0.4 : 1, transition: 'opacity .3s ease' }}>
            {(quote?.lineItems ?? []).map((item, index) => {
              const amount = bigFromFee(item.amount)
              const discount = lineDiscount(item)
              // A €0 line (free cellar room, present-not-measured attic) has nothing to strike through
              const isDiscounted = discount.gt(0) && amount.gt(0)

              return (
                <Stack key={`${item.type}-${index}`} direction="row" justifyContent="space-between" alignItems="baseline" gap=".8rem">

                  <Typography variant="text-sm" color={GRAY_700}>{lineItemLabel(item)}</Typography>

                  {/* Same treatment as ProductItemSummary: discounted price on top, base price struck through below */}
                  <Stack direction="column" alignItems="flex-end">

                    {isDiscounted &&
                      <Typography variant="text-sm" color={GRAY_900} whiteSpace="nowrap" data-testid="measurement-line-discounted-price">
                        {formatPrice(valueAfterDiscount(amount, discount), quote?.currency)}
                      </Typography>
                    }

                    <Typography
                      variant="text-sm"
                      color={isDiscounted ? GRAY_700 : GRAY_900}
                      whiteSpace="nowrap"
                      sx={{ textDecoration: isDiscounted ? 'line-through' : 'none' }}
                    >
                      {formatPrice(amount, quote?.currency)}
                    </Typography>

                  </Stack>

                </Stack>
              )
            })}
          </Stack>
        </Collapse>

      </Stack>
    </Collapse>
  )
}
